import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGuestListing } from "../../hooks/useGuestListing";
import PostForm from "../../components/PostForm";

const ManageListingPage = () => {
  const { management_token } = useParams();
  const navigate = useNavigate();
  const { getGuestListing, updateGuestListing, deleteGuestListing, loading } =
    useGuestListing();
  const [listing, setListing] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  useEffect(() => {
    const fetchListing = async () => {
      setError(null);
      try {
        const data = await getGuestListing(management_token);
        setListing(data);
      } catch (err) {
        setError(err.message || "Failed to fetch listing");
      }
    };
    fetchListing();
  }, [management_token]);

  const handleEdit = () => setEditing(true);
  const handleCancelEdit = () => setEditing(false);

  const handleUpdate = async (
    formValues,
    staticFields,
    categoryId,
    formFields
  ) => {
    try {
      await updateGuestListing(management_token, formValues);
      setSuccessMsg("Listing updated successfully.");
      setEditing(false);
      // Refetch updated listing
      const updated = await getGuestListing(management_token);
      setListing(updated);
    } catch (err) {
      setError(err.message || "Failed to update listing");
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await deleteGuestListing(management_token);
      setSuccessMsg("Listing deleted successfully.");
      setListing(null);
      setTimeout(() => navigate("/"), 2000);
    } catch (err) {
      setError(err.message || "Failed to delete listing");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-600">{error}</div>;
  if (!listing && !successMsg && !loading) {
    return (
      <div className="max-w-2xl mx-auto py-8 px-4">
        <h2 className="text-xl font-bold mb-4">Manage Your Listing</h2>
        <div className="text-red-600">
          This guest post does not exist or has been deleted.
        </div>
      </div>
    );
  }

  // Stats (created/updated, placeholder for views)
  const stats = [
    listing?.created_at && {
      label: "Created",
      value: new Date(listing.created_at).toLocaleString(),
    },
    listing?.updated_at && {
      label: "Updated",
      value: new Date(listing.updated_at).toLocaleString(),
    },
    { label: "Views", value: listing?.views ?? "-" },
    { label: "Post ID", value: listing?.id },
    { label: "Status", value: listing?.status || "Active" },
  ].filter(Boolean);

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <h2 className="text-xl font-bold mb-4">Manage Your Listing</h2>
      {successMsg && <div className="text-green-600 mb-4">{successMsg}</div>}
      {editing && listing ? (
        <PostForm
          mode="edit"
          initialValues={{ ...listing, ...(listing.attributes || {}) }}
          categoryId={listing.category_id}
          onSubmit={handleUpdate}
          onCancel={handleCancelEdit}
          submitLabel="Save Changes"
          loading={loading}
        />
      ) : (
        listing && (
          <>
            {/* Details */}
            <div className="bg-white rounded shadow p-4 mb-4">
              <h3 className="text-lg font-semibold mb-2">
                {listing.post_title}
              </h3>
              <div className="mb-2 text-gray-700">{listing.description}</div>
              {Array.isArray(listing.images) && listing.images.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {listing.images.map((img, i) => (
                    <img
                      key={i}
                      src={img}
                      alt="listing"
                      className="h-20 w-20 object-cover rounded border"
                    />
                  ))}
                </div>
              )}
              <div className="mb-2">
                <span className="font-semibold">Category:</span>{" "}
                {listing.category_id}
              </div>
              <div className="mb-2">
                <span className="font-semibold">Contact Email:</span>{" "}
                {listing.contact_email}
              </div>
              <div className="mb-2">
                <span className="font-semibold">Location:</span> {listing.city},{" "}
                {listing.region}, {listing.country}
              </div>
              {/* Attributes */}
              {listing.attributes && (
                <div className="mb-2">
                  <span className="font-semibold">Attributes:</span>
                  <ul className="ml-4 list-disc">
                    {Object.entries(listing.attributes).map(([k, v]) => (
                      <li key={k}>
                        <span className="font-medium">{k}:</span>{" "}
                        {Array.isArray(v) ? v.join(", ") : String(v)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Stats */}
              <div className="flex flex-wrap gap-4 mt-4">
                {stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="bg-gray-100 rounded px-3 py-1 text-xs"
                  >
                    <span className="font-semibold">{stat.label}:</span>{" "}
                    {stat.value}
                  </div>
                ))}
              </div>
            </div>
            {/* Actions */}
            <div className="flex gap-2">
              <button
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                onClick={handleEdit}
              >
                Edit
              </button>
              <button
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
                onClick={() => setDeleteConfirm(true)}
                disabled={deleting}
              >
                Delete
              </button>
            </div>
            {/* Delete confirmation */}
            {deleteConfirm && (
              <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                <div className="bg-white rounded shadow p-6 max-w-sm w-full">
                  <h3 className="text-lg font-bold mb-4">Delete Listing?</h3>
                  <p className="mb-4">
                    Are you sure you want to delete this listing? This action
                    cannot be undone.
                  </p>
                  <div className="flex gap-2 justify-end">
                    <button
                      className="px-4 py-2 rounded bg-gray-200 text-gray-700"
                      onClick={() => setDeleteConfirm(false)}
                      disabled={deleting}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
                      onClick={handleDelete}
                      disabled={deleting}
                    >
                      {deleting ? "Deleting..." : "Delete"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )
      )}
    </div>
  );
};

export default ManageListingPage;
